import { useEffect, useState } from "react";
import Styles from "./index.module.scss";
import { forgotPasswordFormLogic } from "./formForgotPassword";
import ForgotPasswordForm from "./forgotPasswordForm";
import EmailSentPage from "./emailSentPage";
import { useScreen } from "../../../context/useScreen";
import { useAuthFrontLock } from '../../../context/useAuthFrontContext';
import { useLoader } from '../../../context/useLoader';
import i18n from "../../../utils/translations/i18n";
import { COOKIE_CONSTANT, ERROR_CODE, FALLBACK_LOCALE, PAGE_NAME } from "../../../utils/constant/constant";
import { deleteCookie, redirectToAuthcallback, setCurrentPageCookie } from "../../../utils/utils/utils";

const ForgotPassword = () => {
    const { t } = i18n;
    const [form, setFormState] = useState(JSON.parse(JSON.stringify(forgotPasswordFormLogic)));
    const [errorBarState, setErrorBarState] = useState({isActive:false, errorType:'', message:''});
    const [showEmailSentPage, setShowEmailSentPage] = useState(false);
    const {setDisplayScreen} = useScreen();
    const { showLoader, hideLoader } = useLoader();

    const authFrontLock = useAuthFrontLock();

    const handleBack = () => {
        setDisplayScreen(PAGE_NAME.SIGN_IN);
    }

    useEffect(() => {
        document.title = t('forgot-password.page_title');

        const additionalParams = authFrontLock?.getAdditionalParams();
        setCurrentPageCookie( PAGE_NAME.FORGOT_PASSWORD, additionalParams?.screenId);
      }, []);

    const errorMessages = {
        [ERROR_CODE.NULL_ERROR]: t('forgot_password.mandatory_field_error'),
        [ERROR_CODE.GENERIC_ERROR]: t('forgot_password.generic_error'),
    };

    const handleChange = (event) => {
        setFormState({
            ...form,
            [event.target.name]: {
                ...form[event.target.name],
                value: event.target.value,
                dangerWarn: false,
                errorMessage: null
            }             
        })
    }

    const handleBlur = (event) => {
        const fieldObj = form[event.target.name];
        const { regex, value } = fieldObj;
        const expression = new RegExp(regex);
        const showRegexError = value != '' && !expression.test(value);
        const showNullError = value ==='' && errorBarState.isActive && errorBarState.errorType === ERROR_CODE.NULL_ERROR;
        setFormState({
            ...form,
            [event.target.name]: {
                ...form[event.target.name],
                dangerWarn: showRegexError || showNullError,
                errorMessage: showRegexError ? t('forgot_password.invalid_email_error') : null
            }             
        });
    }

    const validateForm = () => {
        const formObj = {...form};
        let regexError = false, nullError = false;
        
        formObj.formFields.forEach(field => {
            const fieldObj = formObj[field], {regex, value, required} = fieldObj, expression = new RegExp(regex);
            formObj[field].dangerWarn = false;
            formObj[field].errorMessage = '';
            
            if(value !== '' && !expression.test(value)){
                regexError = true;
                formObj[field].dangerWarn = true;
                formObj[field].errorMessage = t('forgot_password.invalid_email_error');
            }
            if(value === '' && required) {
                nullError = true;
                formObj[field].dangerWarn = true;
            }
        })

        if(nullError) showErrorMessage(ERROR_CODE.NULL_ERROR);
        else setErrorBarState({isActive:false, errorType:'', message:''});
        setFormState(formObj);
        return !(regexError || nullError);
    }

    
    const showErrorMessage = (error, email?) => {
        if(error === ERROR_CODE.REAUTHENTICATION_REQUIRED ){
            deleteCookie(COOKIE_CONSTANT.CURRENT_PAGE_COOKIE_KEY);
            authFrontLock && redirectToAuthcallback(authFrontLock, ERROR_CODE.REAUTHENTICATION_REQUIRED, email)
            return;
        }
        if (errorMessages[error]) {
            setErrorBarState({ isActive: true, errorType: error, message: errorMessages[error] });
        }
    };

    const handleSubmit = async (event) => {
        try {
            event.preventDefault();
            setErrorBarState({isActive:false, errorType:'', message:''});
            if(validateForm()){
                showLoader();
                await authFrontLock.changePasswordAsync({
                    email: form.email.value
                })
                setShowEmailSentPage(true);
                const uiLocale = authFrontLock.getUILocales();
                const finalLocale = uiLocale && uiLocale.length > 0 ? uiLocale[0] : FALLBACK_LOCALE;
                localStorage.setItem(COOKIE_CONSTANT.LOCALE_COOKIE_KEY,finalLocale);
            }
        } catch (error:any) {
            if(error.errorCode === ERROR_CODE.REAUTHENTICATION_REQUIRED ){
                showErrorMessage(ERROR_CODE.REAUTHENTICATION_REQUIRED, form.email.value)
            }
            else showErrorMessage(ERROR_CODE.GENERIC_ERROR);
        } finally {
            hideLoader();
        }
    }
    return (
        <div className={Styles.container}>
            { showEmailSentPage ? 
                <EmailSentPage email={form.email.value} handleSubmit={handleSubmit} errorBarState={errorBarState} />
                :
                <ForgotPasswordForm form={form} errorBarState={errorBarState} handleSubmit={handleSubmit} handleChange={handleChange} handleBlur={handleBlur} />
            }
            <span className={Styles.backLink} onClick={handleBack}>
                <a className={Styles.link}>{t('reset_password.back_to_login')}</a>
            </span>
        </div>
    );
};

export default ForgotPassword;